// 5/21: Replaces the old edit tag modal because this new feature only supports notifying other users/departments
// about new received records. Old tag feature has been migrated to new label feature.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Input,
  Form,
  Tag,
  message,
} from 'antd';

import ModalWrapper from './ModalWrapper';
import { validEmail } from '../../global/validation';

const { CheckableTag } = Tag;

const errorStyle = {
  color: 'red',
};

const departmentPointerStyle = {
  fontSize: '12px',
  color: 'grey',
  marginBottom: '10px',
};

// ENG-3339: whether this Notify was opened next to a "Transfer to department"
// entry point, in which case it drops the department chips and is email-only.
// The CALL SITE decides — not a feature-flag read in here. Every surface that
// dispatches NOTIFY without a Transfer sibling (the legacy inbox and referrals
// bars, the full-page RecordViewer, the Chrome-extension RecordViewerIframe)
// omits the flag and keeps the combined modal, so no surface can be left with
// no way to move a record to a department.
const isEmailOnly = (props) => props.modalType.data.emailOnly === true;

class Notify extends Component {
  constructor(props) {
    super(props);
    const { tag } = props.modalType.data;
    // `tag` is overloaded: it holds either the email address a record was notified
    // to OR the name of the department it was transferred to. The combined modal
    // sorts that out in componentDidMount once the department list arrives (it
    // clears the input and preselects the matching chip). Email-only mode has no
    // chips and never fetches that list, so a department name would otherwise sit
    // in the email field with Submit enabled and fail validation on click —
    // ~74k received records in production carry a non-email tag. Seed the input
    // only from something that actually is an email.
    const initialTag = isEmailOnly(props) && tag && !validEmail(tag.trim()) ? '' : tag;
    this.state = {
      tag: initialTag,
      sendTokens: props.modalType.data.record,
      departments: [],
      selectedDepartment: '',
      invalidEmailError: '',
      selectedRecordTypes: props.modalType.data.receiverType,
      departmentValidationError: '',
    };
  }

  componentDidUpdate(prevProps) {
    // Update selectedRecordTypes when modal data changes
    if (prevProps.modalType.data.receiverType !== this.props.modalType.data.receiverType) {
      this.setState({
        selectedRecordTypes: this.props.modalType.data.receiverType,
        departmentValidationError: '',
      });
    }
  }

  componentDidMount = async () => {
    // Email-only mode never renders the chips, so it never needs the department list.
    if (isEmailOnly(this.props)) return;
    const { getOrganizationDepartments } = this.props;
    await getOrganizationDepartments();
    const { allOrganizationDepartments, modalType } = this.props;
    this.setState({ departments: allOrganizationDepartments });
    // eslint-disable-next-line array-callback-return
    allOrganizationDepartments.map((department) => {
      if (department.name === modalType.data.tag) {
        this.setState({
          tag: '',
          selectedDepartment: modalType.data.tag,
        });
      }
    });
  }

  handleNotifyInputChange = (event) => {
    this.setState({
      tag: event.target.value,
      selectedDepartment: '',
      departmentValidationError: '',
    });
  };

  selectDepartment = (index) => {
    const { selectedRecordTypes } = this.state;
    const selectedDept = this.state.departments[index];
    const userSelectedDepartment = selectedDept.name;
    let validationError;

    const recordTypesArray = Array.isArray(selectedRecordTypes)
      ? selectedRecordTypes
      : [selectedRecordTypes];

    // Validate department capabilities based on selected record types
    const missingCapabilities = [];
    if (recordTypesArray.includes('received_fax') && !selectedDept.has_active_fax) {
      missingCapabilities.push('active fax numbers');
    }
    if (recordTypesArray.includes('email_attachment') && !selectedDept.has_email) {
      missingCapabilities.push('email addresses');
    }

    if (missingCapabilities.length > 0) {
      validationError = `The selected department does not have ${missingCapabilities.join(' or ')} to reassign to.`;
    }

    this.setState({
      tag: '',
      selectedDepartment: userSelectedDepartment,
      departmentValidationError: validationError,
    });
  }

  update = async (event) => {
    event.preventDefault();
    const { sendTokens, tag, selectedDepartment } = this.state;
    const { editTag } = this.props;
    // allowing users to submit empty tag field to clear the existing tag
    const trimmedTag = tag ? tag.trim() : '';
    // we're only allowing users to use one method of notification (i.e input or select a department) so depending on which one
    // is defined, we're sending in the correct field of information.
    const notifyInfo = trimmedTag || selectedDepartment;
    if (trimmedTag && !validEmail(trimmedTag)) {
      this.setState({ invalidEmailError: 'Please enter a valid email address' });
      return;
    }
    await editTag(sendTokens, notifyInfo);

    const {
      updatedTag,
      emailValidMessage,
      hideModal,
      noActiveFax,
      noActiveEmail,
      departmentName,
    } = this.props;

    // Check for department capability errors ONLY if user selected a department
    // (not relevant when notifying via email address)
    if (selectedDepartment && (noActiveFax || noActiveEmail)) {
      const errors = [];
      if (noActiveFax) errors.push('active fax numbers');
      if (noActiveEmail) errors.push('email addresses');

      const errorMessage = `The ${departmentName} department does not have ${errors.join(' or ')} to reassign to.`;
      message.error({
        content: errorMessage,
      });
      return;
    }

    if (updatedTag) {
      hideModal();
      if (emailValidMessage === 'Unable to notify: User does not exist or is revoked') {
        message.error({
          content: emailValidMessage,
        });
      } else {
        message.success({
          content: emailValidMessage,
        });
        this.updateRowSelected();
      }
    } else {
      message.error({
        content: emailValidMessage || 'Email doesn\'t exist',
      });
    }
  }

  updateRowSelected() {
    const { tag, fileToken, selectedDepartment } = this.state;
    const { modalType, rowSelected } = this.props;
    // we're only allowing users to use one method of notification (i.e input or select a department) so depending on which one
    // is defined, we're sending in the correct field of information.
    const notifyInfo = tag || selectedDepartment;
    const selectedRow = {
      tag: notifyInfo,
      file_token: fileToken,
      index: modalType.data.record.index,
    };
    rowSelected([selectedRow]);
  }

  renderModal = () => {
    const { isLoading, modalType } = this.props;
    // eslint-disable-next-line object-curly-newline
    const { tag, invalidEmailError, departments, selectedDepartment, departmentValidationError } = this.state;
    if (isLoading) {
      return (<span> Updating tag, Please wait... </span>);
    }

    const { receiverType } = modalType.data;
    const isEmailAttachment = Array.isArray(receiverType)
      ? receiverType.every((type) => type === 'email_attachment')
      : receiverType === 'email_attachment';

    const emailDepartmentNote = `Note: If you select a department, the email attachment will be assigned to the department's first email address, if available.
      If the department has no email addresses, you'll be notified and the assignment will not take place.`;

    const faxDepartmentNote = `Note: If you select a department, the received fax will be assigned to the department's first active fax number, if available.
      If the department has no active fax numbers, you'll be notified and the assignment will not take place.`;

    return (
      <Form
        onFinish={this.update}
        id="notifyUser"
      >
        <p>Want to notify someone in your organization about this record? Type their email address below and we'll notify them.</p>
        <p style={errorStyle}>{invalidEmailError}</p>
        <Form.Item
          name="tag"
          label="Notify"
          hasFeedback
        >
          <div style={{ display: 'none' }}>{this.state.tag}</div>
          <Input
            onChange={this.handleNotifyInputChange}
            value={tag}
            ref={(input) => { if (input != null) input.focus(); }}
            rules={[
              () => ({
                validator(_, email) {
                  if (email && validEmail(email.trim())) return Promise.resolve();
                  return Promise.reject('Please enter a valid email');
                },
              }),
            ]}
          />
        </Form.Item>
        {isEmailOnly(this.props) && (
          // Departments used to be picked here. Anyone who reaches this window looking for
          // them needs to be told where they went, in the place they went looking.
          <p style={departmentPointerStyle}>
            Handing this record to a department instead? Use
            {' '}
            <strong>Transfer to department</strong>
            .
          </p>
        )}
        {!isEmailOnly(this.props) && (
          <>
            <p style={{ marginBottom: '5px' }}>Or select a department from the list below.</p>
            <div style={{ fontSize: '12px', color: 'grey', marginBottom: '10px' }}>
              {isEmailAttachment ? (
                <p>{emailDepartmentNote}</p>
              ) : (
                <p>{faxDepartmentNote}</p>
              )}
            </div>
            {departmentValidationError && (
              <p style={errorStyle}>{departmentValidationError}</p>
            )}
            <Form.Item
              name="department"
            >
              {departments && departments.map((department, index) => (
                <CheckableTag
                  checked={selectedDepartment === department.name}
                  style={{ cursor: 'pointer', border: '1px dotted #1890ff' }}
                  onClick={() => this.selectDepartment(index)}
                >
                  {department.name}
                </CheckableTag>
              ))}
            </Form.Item>
          </>
        )}
      </Form>
    );
  };

  render() {
    const { isLoading, hideModal } = this.props;
    const { selectedDepartment, departmentValidationError, tag } = this.state;
    const customContentStyle = {
      width: '340px',
    };

    // Email-only mode requires an email; combined mode requires an email OR a department.
    const noEmailOrDepartment = isEmailOnly(this.props)
      ? !tag?.trim()
      : (!tag?.trim() && !selectedDepartment);
    const isSubmitDisabled = isLoading
      || noEmailOrDepartment
      || departmentValidationError;

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.update}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={isSubmitDisabled}
        modalTitle="Notify"
        form="notifyUser"
        isDraggable={true}
      >
        {this.renderModal()}
      </ModalWrapper>
    );
  }
}

Notify.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  editTag: PropTypes.func.isRequired,
  rowSelected: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  emailValidMessage: PropTypes.string.isRequired,
  updatedTag: PropTypes.bool.isRequired,
  allOrganizationDepartments: PropTypes.array.isRequired,
  getOrganizationDepartments: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  noActiveFax: PropTypes.bool,
  departmentName: PropTypes.string,
  noActiveEmail: PropTypes.bool,
};

Notify.defaultProps = {
  error: false,
  errorMessage: '',
  noActiveFax: false,
  departmentName: '',
  noActiveEmail: false,
};

export default connect(state => ({
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  updatedTag: state.records.updatedTag,
  data: state.labels.data,
  departments: state.userProfile.departments,
  allOrganizationDepartments: state.userProfile.allOrganizationDepartments,
  error: state.records.error,
  errorMessage: state.records.errorMessage,
  noActiveFax: state.records.noActiveFax,
  departmentName: state.records.departmentName,
  noActiveEmail: state.records.noActiveEmail,
}))(Notify);
